import Icon from '@rio-cloud/rio-uikit/components/icon/Icon';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { gaPush, QUESTION_PANEL_ACTIONS, QUESTION_PANEL_CATEGORY } from '@/configuration/setup/googleAnalytics';

import {
  EXAMPLE_QUESTION_PANEL_ID,
  EXAMPLE_QUESTION_PANEL_MESSAGES,
  EXAMPLE_QUESTION_PANEL_TEST_IDS,
} from '../constants';
import { ExampleQuestionsPanelToggleButton } from './ExampleQuestionsPanelToggle.styles';
import { TExampleQuestionsPanelToggleProps } from './types';

const ExampleQuestionsPanelToggle = ({
  isPanelOpen,
  isDisabled,
  shouldRestoreFocus,
  onOpen,
  onClose,
  onFocusRestored,
}: TExampleQuestionsPanelToggleProps) => {
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // INFO: Closing the panel after a message has been sent hands focus back to the Panel Toggle,
    // which is the only entry point left in the thread. shouldRestoreFocus is the one-shot request
    // and onFocusRestored clears it so a later re-render cannot steal focus again.
    if (!shouldRestoreFocus) return;

    toggleRef.current?.focus();
    onFocusRestored();
  }, [shouldRestoreFocus, onFocusRestored]);

  const handleClick = useCallback(() => {
    if (isDisabled) return;

    if (isPanelOpen) {
      onClose();
      return;
    }

    gaPush({
      category: QUESTION_PANEL_CATEGORY,
      action: QUESTION_PANEL_ACTIONS.OPEN_PANEL_TOGGLE,
      label: '',
    });

    onOpen();
  }, [isDisabled, isPanelOpen, onClose, onOpen]);

  return (
    <ExampleQuestionsPanelToggleButton
      type="button"
      ref={toggleRef}
      onClick={handleClick}
      aria-disabled={isDisabled}
      aria-expanded={isPanelOpen}
      aria-controls={EXAMPLE_QUESTION_PANEL_ID}
      data-testid={EXAMPLE_QUESTION_PANEL_TEST_IDS.panelToggle}
    >
      <Icon iconName="icon-question-sign" aria-hidden="true" />
      <FormattedMessage id={EXAMPLE_QUESTION_PANEL_MESSAGES.panelToggle} />
    </ExampleQuestionsPanelToggleButton>
  );
};

export default ExampleQuestionsPanelToggle;
